import { SurveyQuestion } from './types';

/**
 * Board Director Survey — Todd (passive board director & FLL parent)
 * Context: OARA re-org assessment for next season planning.
 *
 * Flow rationale:
 *  1. Warm opener      — easy reflection, sets a welcoming tone
 *  2. Meeting availability — practical, low-stakes commitment question
 *  3. Active contributions — what areas interest them
 *  4. FTC opportunity  — specific new program, concrete ask
 *  5. Overall role     — big-picture decision, now informed by thinking through 2-4
 *  6. Open space       — anything else on their mind
 */
export const boardDirectorQuestions: SurveyQuestion[] = [

  // ── 1. Warm opener ──────────────────────────────────────────────────────────
  {
    id: 'q0_experience',
    type: 'radio',
    label: 'How has your overall experience as a board member at OARA Youth Innovation Lab been so far?',
    required: false,
    options: [
      { value: 'very_positive',  label: 'Very positive — it has been rewarding and meaningful' },
      { value: 'mostly_positive', label: 'Mostly positive — some challenges but overall good' },
      { value: 'mixed',          label: 'Mixed — there have been both highs and challenges' },
      { value: 'limited',        label: 'Limited involvement — I have not been very active' },
      { value: 'unsure',         label: 'Not sure / hard to say' },
    ],
  },

  // ── 2. Meeting availability ──────────────────────────────────────────────────
  {
    id: 'q2_meeting',
    type: 'radio',
    label: 'As we restructure, we are planning regular monthly 1-hour board meetings. Are you able to commit to attending?',
    required: true,
    options: [
      { value: 'yes_inperson', label: 'Yes — in person' },
      { value: 'yes_virtual',  label: 'Yes — virtually (video call)' },
      { value: 'yes_either',   label: 'Yes — either works' },
      { value: 'most_months',  label: 'Most months — occasional conflicts may come up' },
      { value: 'no',           label: 'No — I am not able to commit to monthly meetings right now' },
    ],
  },
  {
    id: 'q2_comments',
    type: 'textarea',
    label: 'Any comments on your availability for meetings?',
    required: false,
    placeholder: 'Optional — e.g. scheduling constraints, preferred times, or anything helpful for us to know...',
  },

  // ── 3. Areas to contribute ──────────────────────────────────────────────────
  {
    id: 'q3_roles',
    type: 'checkbox',
    label: 'Are there any areas where you would like to contribute more actively to OARA? (Select all that apply)',
    required: false,
    options: [
      { value: 'public_relations',      label: 'Public Relations — representing OARA at community events and in media' },
      { value: 'corporate_sponsorship', label: 'Corporate Sponsorships — reaching out to larger companies for funding' },
      { value: 'small_biz_sponsorship', label: 'Local / Small Business Sponsorships — connecting with Vernon-area businesses' },
      { value: 'community_outreach',    label: 'Community Outreach — building relationships with schools, parents, and families' },
      { value: 'grant_writing',         label: 'Grant Writing Support — helping research and apply for grants' },
      { value: 'event_coordination',    label: 'Event Coordination — helping plan and run OARA events and competitions' },
      { value: 'none',                  label: 'None at this time' },
    ],
  },
  {
    id: 'q3_comments',
    type: 'textarea',
    label: 'Any other ways you would like to contribute, or roles not listed above?',
    required: false,
    placeholder: 'Optional — feel free to suggest anything, even if it is informal...',
  },

  // ── 4. FTC mentorship opportunity ───────────────────────────────────────────
  {
    id: 'q4_ftc',
    type: 'radio',
    label: 'OARA has a significant chance of participating in FTC (FIRST Tech Challenge) next season — a hands-on robotics competition for older students. If we do, would you consider mentoring a team?',
    required: false,
    options: [
      { value: 'yes_lead',    label: 'Yes — I would be willing to be a lead mentor' },
      { value: 'yes_support', label: 'Yes — as a supporting mentor alongside others' },
      { value: 'maybe',       label: 'Maybe — I would need to know more about the time commitment first' },
      { value: 'no',          label: 'No — I am not in a position to mentor at this time' },
    ],
  },
  {
    id: 'q4_comments',
    type: 'textarea',
    label: 'Anything you would like to share about FTC — experience, availability, or questions:',
    required: false,
    placeholder: 'Optional — e.g. relevant technical background, availability, or what you would want to know first...',
  },

  // ── 5. Overall role — big-picture closing decision ───────────────────────────
  {
    id: 'q1_role',
    type: 'radio',
    label: 'Having reflected on the questions above — what kind of role would you like to have with OARA going into the next season?',
    required: true,
    options: [
      { value: 'active',     label: 'Active engagement — hands-on, regular involvement in programs and decisions' },
      { value: 'medium',     label: 'Medium engagement — involved in key decisions, occasional on-the-ground participation' },
      { value: 'low',        label: 'Low engagement — available for advisory input and major decisions only' },
      { value: 'step_back',  label: 'I would like to step back from my board role' },
      { value: 'unsure',     label: 'Not sure yet — I need a bit more information to decide' },
    ],
  },
  {
    id: 'q1_comments',
    type: 'textarea',
    label: 'Any thoughts on your preferred role or level of involvement?',
    required: false,
    placeholder: 'Optional — share anything that would help us understand your availability or preferences...',
  },

  // ── 6. Open space ────────────────────────────────────────────────────────────
  {
    id: 'q5_feedback',
    type: 'textarea',
    label: 'Is there anything else you would like to share with the OARA team as we plan for the next season?',
    required: false,
    placeholder: 'Any thoughts on the makerspace transition, FLL/FTC participation, grant opportunities, or the overall direction of OARA...',
  },

];
